/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import PulseLoader from 'react-spinners/PulseLoader';

// app modules: components
import Collection from 'components/Collection';
import CollectionTypes from 'components/Collection/CollectionTypes';
import ErrorRouter from 'components/ErrorRouter';
import HeadContent from 'components/HeadContent';

// app modules: hooks
import usePrevious from 'hooks/usePrevious';

// app modules: queries
import COLLECTION_QUERY from 'components/Collection/CollectionGraphQL';

// app modules: services
import route from 'services/route';
import history from 'services/history';

// app modules: constants
import { COLOURS } from 'constants/index';

const isSSR = typeof window === 'undefined';

function CollectionView(props) {
  const { shareKey } = props;
  const baseUrl = useSelector(state => state.config.baseUrl);
  const country = useSelector(state => state.user.geolocation.countryCode);
  const userIsCkbkEditor = useSelector(state =>
    state.user.permissions?.includes('collections:edit'),
  );
  const userId = useSelector(state => state.user.id);
  const [mounted, setMounted] = useState(false);

  // We'll load the user collection so we can draw quickly.... however this doesn't have items in it
  // and it will be empty on the server
  const userCollection = useSelector(state => state.user.collections[`collection-${props.id}`]);

  // So we'll fetch the items from the server.
  // We also handle the case where you are viewing someone elses collection -
  // hence the request for data thats probably already in userCollection.
  const { error, data, refetch } = useQuery(COLLECTION_QUERY, {
    variables: {
      id: parseInt(props.id, 10),
      shareKey,
      country,
      _authenticated: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  // Re-run the graphql query if the collection changes.
  const updatedAt = userCollection ? userCollection.updated_at : null;
  const previousUpdatedAt = usePrevious(updatedAt);
  const visibility = userCollection ? userCollection.visibility : null;
  const previousVisibility = usePrevious(visibility);
  useEffect(() => {
    if (
      (previousUpdatedAt && updatedAt !== previousUpdatedAt) ||
      (typeof previousPublicPermission !== 'undefined' && visibility !== previousVisibility)
    ) {
      refetch();
    }
  });

  useEffect(() => {
    setMounted(true);
  }, []);
  // The type parameter may contain the title of the collection - in which case we ignore it.
  const type = CollectionTypes[props.type] ? props.type : null;

  const userCollectionWithItems = userCollection
    ? { ...userCollection, items: { books: [], people: [], contents: [] } }
    : null;
  const collection = data && data.Collection ? data.Collection : userCollectionWithItems;

  useEffect(
    () => {
      if (collection) {
        if (
          (!shareKey && collection.visibility === 'unlisted' && collection.share_key) ||
          (shareKey && collection.visibility !== 'unlisted')
        ) {
          history.replace(route.collection.view(collection, type));
        }
      }
    },
    [shareKey, collection, type],
  );

  const errorIsPrivateCollection =
    error?.graphQLErrors[0]?.extensions?.code === 'FORBIDDEN_COLLECTION';

  // On the SSR for a private collection the server doesn't know who the user is, so there will be
  // a FORBIDDEN_COLLECTION error.
  // Treat this as the loading state and let the client refresh... but only AFTER it has mounted
  // to avoid mismatch between server and client markup.
  const loadingScreen = (
    <div
      style={{
        width: '100%',
        minHeight: '100vw',
        display: 'flex',
        justifyContent: 'center',
        marginTop: 80,
      }}
    >
      {error && (!errorIsPrivateCollection || mounted) ? (
        <ErrorRouter error={error} />
      ) : (
        <PulseLoader loading color={COLOURS.STABLE} sizeUnit="px" size={10} />
      )}
    </div>
  );
  if ((collection && isSSR) || (collection && mounted)) {
    const title = collection.name;
    let { plain_description } = collection;
    if (collection.items) {
      const contentItemCount = collection.items.contents.length;
      if (!plain_description && contentItemCount) {
        plain_description = collection.items.contents[0].title;
        if (contentItemCount > 1) {
          plain_description += `and ${contentItemCount - 1} other recipes`;
        }
      }
    }
    const structuredData =
      collection.visibility === 'public'
        ? {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: collection.items.contents.map((content, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `${baseUrl}${route.content(content.id, content.title)}`,
            })),
          }
        : null;
    const asCkbkEditor =
      collection.visibility === 'private' &&
      userIsCkbkEditor &&
      collection.user_id !== userId &&
      userId > 0;
    return (
      <React.Fragment>
        <HeadContent
          contents={{
            title,
            description: plain_description,
            url: route.collection.view(collection, type),
            structuredData,
          }}
        />
        <Collection asCkbkEditor={asCkbkEditor} collection={collection} type={type} />
      </React.Fragment>
    );
  }
  return loadingScreen;
}

CollectionView.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

export default CollectionView;
